import { c as _c } from "react/compiler-runtime";
import { Chips, NumberInput, Separator, Slider } from 'platform/components';
import { HStack, VStack } from 'platform/foundation-tw';
import { useState } from 'react';
import { isNil } from 'ramda';
import { isArray, isString } from 'ramda-adjunct';
import { suffixTestId, type Nullish } from 'shared/web';
import { useFilterOnChipsChange } from '../../hooks/useFilterOnChipsChange';
import { useSliderTicks } from '../../hooks/useSliderTicks';
import type { EagFilterProps } from '../../types/EagFilterProps';
import { useSliderFilter } from './hooks/useSliderFilter';
export type NumberSliderRangeProps = EagFilterProps & {
  min: number;
  max: number;
  minOpen: boolean;
  maxOpen: boolean;
  unit: string | null;
  stepSize: number | null;
  steps: number[];
  markerSize: number | null;
  markers: number[];
  decimalsAllowed: boolean;
  inputsVisible: boolean;
  quickFilters?: Array<{
    value: string;
    label: string;
  }>;
};
const FILTER_VALUE_KEY = 'value';
export function NumberSliderRangeFilter(props) {
  const $ = _c(79);
  const ticks = useSliderTicks(props.min, props.max, props.markerSize, props.markers);
  const model = props.value[FILTER_VALUE_KEY] as [number | null, number | null] | string | Nullish;
  let t0;
  if ($[0] !== props.unit) {
    t0 = value => props.unit ? `${value} [${props.unit}]` : `${value}`;
    $[0] = props.unit;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const valueFormatter = t0;
  let t1;
  if ($[2] !== props.max || $[3] !== props.min) {
    t1 = [props.min, props.max];
    $[2] = props.max;
    $[3] = props.min;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const [localSliderValue, setLocalSliderValue] = useState(t1);
  let t2;
  if ($[5] !== props) {
    t2 = newValue => {
      props.onChange({
        [FILTER_VALUE_KEY]: newValue
      });
    };
    $[5] = props;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const onModelChange = t2;
  let t3;
  if ($[7] !== model || $[8] !== onModelChange || $[9] !== props.max || $[10] !== props.min) {
    t3 = {
      model,
      onModelChange,
      min: props.min,
      max: props.max
    };
    $[7] = model;
    $[8] = onModelChange;
    $[9] = props.max;
    $[10] = props.min;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const [filterValue, setFilterValue] = useSliderFilter(t3);
  let t4;
  if ($[12] !== props.max || $[13] !== props.min || $[14] !== setFilterValue) {
    t4 = {
      setFilterValue: newValue_0 => {
        setFilterValue(newValue_0);
        setLocalSliderValue([props.min, props.max]);
      },
      defaultValue: null
    };
    $[12] = props.max;
    $[13] = props.min;
    $[14] = setFilterValue;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const {
    onChipsChange
  } = useFilterOnChipsChange(t4);
  let t5;
  if ($[16] !== setFilterValue) {
    t5 = value_0 => {
      setLocalSliderValue(value_0);
      setFilterValue(value_0);
    };
    $[16] = setFilterValue;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const onSliderValueChange = t5;
  let t6;
  if ($[18] !== filterValue || $[19] !== onSliderValueChange || $[20] !== props.max || $[21] !== props.min) {
    t6 = value_1 => {
      const valueToSet = value_1 === null || isNaN(value_1) ? null : value_1;
      if (isNil(filterValue)) {
        onSliderValueChange([valueToSet, null]);
      }
      if (isArray(filterValue)) {
        const isFromNilOrLowestPossible = isNil(valueToSet) || valueToSet === props.min;
        const isToNilOrLowestPossible = isNil(filterValue?.[1]) || filterValue?.[1] === props.max;
        if (isFromNilOrLowestPossible && isToNilOrLowestPossible) {
          onSliderValueChange(null);
        } else {
          onSliderValueChange([valueToSet, filterValue?.[1] === 0 ? null : filterValue?.[1]]);
        }
      }
      if (isString(filterValue)) {
        onSliderValueChange([valueToSet, props.max]);
      }
    };
    $[18] = filterValue;
    $[19] = onSliderValueChange;
    $[20] = props.max;
    $[21] = props.min;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const onFromInputChange = t6;
  let t7;
  if ($[23] !== filterValue || $[24] !== onSliderValueChange || $[25] !== props.max || $[26] !== props.min) {
    t7 = value_2 => {
      const valueToSet_0 = value_2 === null || isNaN(value_2) ? null : value_2;
      if (isNil(filterValue)) {
        onSliderValueChange([null, valueToSet_0]);
      }
      if (isArray(filterValue)) {
        const isFromNilOrLowestPossible_0 = isNil(filterValue?.[0]) || filterValue?.[0] === props.min;
        const isToNilOrLowestPossible_0 = isNil(valueToSet_0) || valueToSet_0 === props.max;
        if (isFromNilOrLowestPossible_0 && isToNilOrLowestPossible_0) {
          onSliderValueChange(null);
        } else {
          onSliderValueChange([filterValue?.[0] === 0 ? null : filterValue?.[0], valueToSet_0]);
        }
      }
      if (isString(filterValue)) {
        onSliderValueChange([props.min, valueToSet_0]);
      }
    };
    $[23] = filterValue;
    $[24] = onSliderValueChange;
    $[25] = props.max;
    $[26] = props.min;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const onToInputChange = t7;
  let t8;
  if ($[28] !== model) {
    t8 = isString(model) ? [model] : null;
    $[28] = model;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const isQuickFilterValue = t8;
  let t9;
  if ($[30] !== localSliderValue || $[31] !== props.max || $[32] !== props.min) {
    t9 = isArray(localSliderValue) ? [localSliderValue?.[0] ?? props.min, localSliderValue?.[1] ?? props.max] : [props.min, props.max];
    $[30] = localSliderValue;
    $[31] = props.max;
    $[32] = props.min;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const isSliderValue = t9;
  let t10;
  if ($[34] !== filterValue) {
    t10 = isArray(filterValue) ? filterValue?.[0] : null;
    $[34] = filterValue;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  const isFromValue = t10;
  let t11;
  if ($[36] !== filterValue) {
    t11 = isArray(filterValue) ? filterValue?.[1] : null;
    $[36] = filterValue;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  const isToValue = t11;
  let t12;
  if ($[38] !== isQuickFilterValue || $[39] !== onChipsChange || $[40] !== props) {
    t12 = props.quickFilters && props.quickFilters.length > 0 && <><HStack><Chips isDisabled={props.isDisabled} value={isQuickFilterValue} options={props.quickFilters} onChange={onChipsChange} isMultiple={false} data-testid={suffixTestId("number-range-filter-quickFilters", props)} isDeselectable={true} /></HStack><Separator /></>;
    $[38] = isQuickFilterValue;
    $[39] = onChipsChange;
    $[40] = props;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  const t13 = props.isDisabled;
  const t14 = props.min;
  const t15 = props.max;
  const t16 = props.stepSize ?? props.steps ?? 1;
  const t17 = isSliderValue as [number, number];
  let t18;
  if ($[42] !== props) {
    t18 = suffixTestId("number-range-filter-slider", props);
    $[42] = props;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== onSliderValueChange || $[45] !== props.isDisabled || $[46] !== props.max || $[47] !== props.min || $[48] !== t16 || $[49] !== t17 || $[50] !== t18 || $[51] !== ticks || $[52] !== valueFormatter) {
    t19 = <Slider isDisabled={t13} isRange={true} min={t14} max={t15} step={t16} value={t17} allowMoveStart={true} onChange={onSliderValueChange} formatValue={valueFormatter} ticks={ticks} onlyEdges={true} displayValue="auto" data-testid={t18} />;
    $[44] = onSliderValueChange;
    $[45] = props.isDisabled;
    $[46] = props.max;
    $[47] = props.min;
    $[48] = t16;
    $[49] = t17;
    $[50] = t18;
    $[51] = ticks;
    $[52] = valueFormatter;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  const t20 = props.min;
  const t21 = props.max;
  const t22 = props.isDisabled;
  let t23;
  if ($[54] !== props) {
    t23 = suffixTestId("number-range-filter-from", props);
    $[54] = props;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== isFromValue || $[57] !== onFromInputChange || $[58] !== props.isDisabled || $[59] !== props.max || $[60] !== props.min || $[61] !== t23) {
    t24 = <NumberInput value={isFromValue} onChange={onFromInputChange} isStepperVisible={true} minStepperValue={t20} maxStepperValue={t21} isDisabled={t22} data-testid={t23} />;
    $[56] = isFromValue;
    $[57] = onFromInputChange;
    $[58] = props.isDisabled;
    $[59] = props.max;
    $[60] = props.min;
    $[61] = t23;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  const t25 = props.min;
  const t26 = props.max;
  const t27 = props.isDisabled;
  let t28;
  if ($[63] !== props) {
    t28 = suffixTestId("number-range-filter-to", props);
    $[63] = props;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== isToValue || $[66] !== onToInputChange || $[67] !== props.isDisabled || $[68] !== props.max || $[69] !== props.min || $[70] !== t28) {
    t29 = <NumberInput value={isToValue} onChange={onToInputChange} isStepperVisible={true} minStepperValue={t25} maxStepperValue={t26} isDisabled={t27} data-testid={t28} />;
    $[65] = isToValue;
    $[66] = onToInputChange;
    $[67] = props.isDisabled;
    $[68] = props.max;
    $[69] = props.min;
    $[70] = t28;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== t24 || $[73] !== t29) {
    t30 = <HStack spacing={2}>{t24}{t29}</HStack>;
    $[72] = t24;
    $[73] = t29;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  let t31;
  if ($[75] !== t12 || $[76] !== t19 || $[77] !== t30) {
    t31 = <VStack spacing={5}>{t12}{t19}{t30}</VStack>;
    $[75] = t12;
    $[76] = t19;
    $[77] = t30;
    $[78] = t31;
  } else {
    t31 = $[78];
  }
  return t31;
}